import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useAuth } from 'contexts/AuthContext';
import { useErpThemeOptional } from 'contexts/ErpThemeContext';
import { resolveTenantBranding } from 'config/tenantBranding';
import api from 'services/api';
import { base64UrlDecode } from 'utils/common_util';
import {
  ENV_SETTINGS_CHANGED_EVENT,
  loadEnvSettings,
  loadEnvSettingsAsync,
} from 'system/envSettingsStore';
import { loadScreenLockSettings, SCREEN_LOCK_THEME } from 'system/screenLockSettings';
import LockOverlay from 'components/common/LockOverlay';

const KEY_ACTIVITY = 'erp.session.lastActivityAt';
const KEY_LOCKED = 'erp.session.locked';
const CHANNEL = 'erp-session-idle';

const SessionIdleContext = createContext(null);

function isDesktopWeb() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua)) return false;
  if (navigator.maxTouchPoints > 1 && /Macintosh/i.test(ua)) return false;
  return true;
}

function readActivityAt() {
  const n = Number(localStorage.getItem(KEY_ACTIVITY) || '');
  return Number.isFinite(n) && n > 0 ? n : Date.now();
}

function writeActivityAt(ts) {
  localStorage.setItem(KEY_ACTIVITY, String(ts));
}

function readLocked() {
  return localStorage.getItem(KEY_LOCKED) === '1';
}

function writeLocked(locked) {
  localStorage.setItem(KEY_LOCKED, locked ? '1' : '0');
}

function tokenExpired() {
  const token = localStorage.getItem('token');
  if (!token) return true;
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return true;
    const payload = JSON.parse(base64UrlDecode(parts[1]));
    if (!payload.exp) return false;
    return Date.now() >= payload.exp * 1000;
  } catch {
    return true;
  }
}

function defaultHardLogout() {
  return { enabled: true, timeoutSec: 3600 };
}

function readHardLogout(compCd) {
  const sec = loadEnvSettings(compCd || '').security || {};
  const timeout = Number(sec.hardLogoutTimeoutSec);
  return {
    enabled: sec.hardLogoutEnabled !== false,
    timeoutSec: Number.isFinite(timeout) && timeout > 0 ? timeout : 3600,
  };
}

export function SessionIdleProvider({ children }) {
  const { isAuthenticated, user, logout, compCd } = useAuth();
  const erpTheme = useErpThemeOptional();
  const colorMode = erpTheme?.colorMode || 'light';
  const desktop = isDesktopWeb();
  const [locked, setLocked] = useState(() => desktop && readLocked());
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [settingsTick, setSettingsTick] = useState(0);
  const lastWriteRef = useRef(0);
  const channelRef = useRef(null);

  const soft = loadScreenLockSettings(compCd, user?.id);
  const hard = !compCd ? defaultHardLogout() : readHardLogout(compCd);
  void settingsTick;

  const branding = useMemo(
    () => resolveTenantBranding(compCd, colorMode),
    [compCd, colorMode]
  );

  const postAll = useCallback((msg) => {
    try {
      channelRef.current?.postMessage(msg);
    } catch {
      /* ignore */
    }
  }, []);

  const applyLock = useCallback(
    (next) => {
      writeLocked(next);
      setLocked(next);
      if (next) setError('');
      postAll({ type: next ? 'lock' : 'unlock' });
    },
    [postAll]
  );

  const lockNow = useCallback(() => {
    if (!desktop || !isAuthenticated) return;
    applyLock(true);
  }, [desktop, isAuthenticated, applyLock]);

  const goLogin = useCallback(
    (broadcast = true) => {
      writeLocked(false);
      setLocked(false);
      if (broadcast) postAll({ type: 'hard-logout' });
      logout();
      window.location.href = '/login';
    },
    [logout, postAll]
  );

  const markActivity = useCallback(() => {
    if (!desktop || !isAuthenticated || locked) return;
    const now = Date.now();
    if (now - lastWriteRef.current < 1000) return;
    lastWriteRef.current = now;
    writeActivityAt(now);
    postAll({ type: 'activity', at: now });
  }, [desktop, isAuthenticated, locked, postAll]);

  useEffect(() => {
    if (!desktop || !isAuthenticated || !compCd) return undefined;
    loadEnvSettingsAsync(compCd).then(() => setSettingsTick((n) => n + 1)).catch(() => {});
    const onEnv = () => setSettingsTick((n) => n + 1);
    window.addEventListener(ENV_SETTINGS_CHANGED_EVENT, onEnv);
    return () => window.removeEventListener(ENV_SETTINGS_CHANGED_EVENT, onEnv);
  }, [desktop, isAuthenticated, compCd]);

  useEffect(() => {
    if (!desktop || !isAuthenticated) {
      setLocked(false);
      writeLocked(false);
      return undefined;
    }
    if (!localStorage.getItem(KEY_ACTIVITY)) writeActivityAt(Date.now());
    let ch;
    try {
      ch = new BroadcastChannel(CHANNEL);
      channelRef.current = ch;
      ch.onmessage = (ev) => {
        const msg = ev?.data || {};
        if (msg.type === 'lock') setLocked(true);
        if (msg.type === 'unlock') {
          setLocked(false);
          setError('');
        }
        if (msg.type === 'hard-logout') goLogin(false);
      };
    } catch {
      channelRef.current = null;
    }
    const onStorage = (e) => {
      if (e.key === KEY_LOCKED) setLocked(e.newValue === '1');
    };
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('storage', onStorage);
      try {
        ch?.close();
      } catch {
        /* ignore */
      }
      channelRef.current = null;
    };
  }, [desktop, isAuthenticated, goLogin]);

  useEffect(() => {
    if (!desktop || !isAuthenticated) return undefined;
    const onAct = () => markActivity();
    window.addEventListener('mousedown', onAct, true);
    window.addEventListener('keydown', onAct, true);
    window.addEventListener('scroll', onAct, true);
    window.addEventListener('touchstart', onAct, true);
    window.addEventListener('mousemove', onAct, true);
    return () => {
      window.removeEventListener('mousedown', onAct, true);
      window.removeEventListener('keydown', onAct, true);
      window.removeEventListener('scroll', onAct, true);
      window.removeEventListener('touchstart', onAct, true);
      window.removeEventListener('mousemove', onAct, true);
    };
  }, [desktop, isAuthenticated, markActivity]);

  useEffect(() => {
    if (!desktop || !isAuthenticated) return undefined;
    const onKey = (e) => {
      if (e.ctrlKey && e.shiftKey && (e.key === 'L' || e.key === 'l')) {
        e.preventDefault();
        lockNow();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [desktop, isAuthenticated, lockNow]);

  useEffect(() => {
    if (!desktop || !isAuthenticated) return undefined;
    const tick = () => {
      if (tokenExpired()) {
        goLogin();
        return;
      }
      const idleMs = Date.now() - readActivityAt();
      if (hard.enabled && idleMs >= hard.timeoutSec * 1000) {
        goLogin();
        return;
      }
      if (!locked && soft.enabled && idleMs >= soft.timeoutSec * 1000) {
        applyLock(true);
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [desktop, isAuthenticated, locked, soft, hard, applyLock, goLogin]);

  const submitPassword = useCallback(
    async (password) => {
      if (tokenExpired()) {
        goLogin();
        return;
      }
      setSubmitting(true);
      setError('');
      try {
        const res = await api.post('/auth/verify-password', { password });
        const data = res?.data || {};
        if (data.forceLogout) {
          goLogin();
          return;
        }
        if (data.valid) {
          writeActivityAt(Date.now());
          applyLock(false);
          return;
        }
        setError(data.message || '비밀번호가 올바르지 않습니다.');
      } catch (e) {
        if (e?.status === 401) {
          goLogin();
          return;
        }
        setError(e?.message || '비밀번호 확인에 실패했습니다.');
      } finally {
        setSubmitting(false);
      }
    },
    [applyLock, goLogin]
  );

  const value = useMemo(
    () => ({ locked, lockNow, enabled: desktop && isAuthenticated }),
    [locked, lockNow, desktop, isAuthenticated]
  );

  const themeKey =
    soft.theme === SCREEN_LOCK_THEME.TENANT_BRAND && branding?.logoUrl
      ? SCREEN_LOCK_THEME.TENANT_BRAND
      : SCREEN_LOCK_THEME.MINIMAL_DARK;

  return (
    <SessionIdleContext.Provider value={value}>
      {children}
      {desktop && isAuthenticated && locked ? (
        <LockOverlay
          themeKey={themeKey}
          logoUrl={branding?.logoUrl}
          logoAlt={branding?.logoAlt}
          colorMode={colorMode}
          error={error}
          submitting={submitting}
          onSubmit={submitPassword}
        />
      ) : null}
    </SessionIdleContext.Provider>
  );
}

export function useSessionIdle() {
  const ctx = useContext(SessionIdleContext);
  if (!ctx) {
    return { locked: false, lockNow: () => {}, enabled: false };
  }
  return ctx;
}
